import { observable, configure, action, runInAction } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

class DocumentStore extends LocalModel {
    constructor() {
        super('document', {
            namePlural: 'documents',
            namePluralReal: 'documents',
            sort: '-createdDate',
            limit: 100,

            api: {
                search: {
                    url: '/api/documents/',
                    params: {
                        limit: 100,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/documents/',
                    params: {},
                },
                save: {
                    url: '/api/documents/',
                    params: {},
                },
                delete: {
                    url: '/api/documents/',
                    params: {},
                },
            },
        });
    }

    @observable newDocument = {};

    @observable document = {};

    @observable documents = [];

    /**
     * Check if a document is expired
     * @param {Date|string} expiryDate - The expiry date to check
     * @returns {boolean} - True if expired
     */
    isExpired(expiryDate) {
        if (!expiryDate) return false;
        const expiry = new Date(expiryDate);
        const now = new Date();
        return expiry < now;
    }

    /**
     * Check if a document is expiring soon (within 30 days)
     * @param {Date|string} expiryDate - The expiry date to check
     * @returns {boolean} - True if expiring within 30 days
     */
    isExpiringSoon(expiryDate) {
        if (!expiryDate) return false;
        const expiry = new Date(expiryDate);
        const now = new Date();
        const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        return expiry > now && expiry <= thirtyDaysFromNow;
    }

    /**
     * Get document type display name
     * @param {string} documentType - The document type key
     * @returns {string} - Human readable document type name
     */
    getDocumentTypeName(documentType) {
        const typeNames = {
            'driving-license': 'Driving License',
            'fishing-license': 'Fishing License',
            'hunting-license': 'Hunting License',
            'boat-license': 'Boat License',
            'weapons-license': 'Weapons License',
            'hunting-permit': 'Hunting Permit',
            'weapons-permit': 'Weapons Permit',
            'travel-permit': 'Travel Permit',
            'event-ticket': 'Event Ticket',
            'travel-ticket': 'Travel Ticket',
            'membership-card': 'Membership Card',
            'certificate': 'Certificate',
            'id-card': 'ID Card',
            'passport': 'Passport',
            'other': 'Other',
        };
        return typeNames[documentType] || documentType || 'Unknown';
    }

    /**
     * Get document type icon
     * @param {string} documentType - The document type key
     * @returns {string} - FontAwesome icon class
     */
    getDocumentTypeIcon(documentType) {
        const typeIcons = {
            'driving-license': 'fa-duotone fa-car',
            'fishing-license': 'fa-duotone fa-fish',
            'hunting-license': 'fa-duotone fa-deer',
            'boat-license': 'fa-duotone fa-ship',
            'weapons-license': 'fa-duotone fa-gun',
            'hunting-permit': 'fa-duotone fa-deer',
            'weapons-permit': 'fa-duotone fa-gun',
            'travel-permit': 'fa-duotone fa-plane',
            'event-ticket': 'fa-duotone fa-ticket',
            'travel-ticket': 'fa-duotone fa-plane-departure',
            'membership-card': 'fa-duotone fa-id-card',
            'certificate': 'fa-duotone fa-certificate',
            'id-card': 'fa-duotone fa-id-card',
            'passport': 'fa-duotone fa-passport',
            'other': 'fa-duotone fa-file',
        };
        return typeIcons[documentType] || 'fa-duotone fa-file';
    }
}

const store = new DocumentStore();
export default store;
